// frontend/modules/customer-notification/components/trigger/ManualNotificationTrigger.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Space, Divider, Typography, Result, Spin, Select, Modal } from 'antd';
import { SendOutlined, ApiOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNotificationTrigger } from '../../hooks/useNotificationTrigger';
import { useGetAllQueryServices } from '@/shared/api/client/notification-process-service-controller';
import { QueryServiceInfo, NotificationPlatforms, Languages } from '@/shared/api/model';
import DynamicFilterForm from '../scheduler/DynamicFilterForm';
import PlatformSelector from '../common/PlatformSelector';
import NotificationTemplateSelector from '../common/NotificationTemplateSelector';
import { executeNotificationProcess, previewTemplateParameters } from '@/shared/api/client/notification-process-service-controller';

const { Title, Text } = Typography;
const { Option } = Select;

interface ManualNotificationTriggerProps {
    onClose: () => void;
}

const ManualNotificationTrigger: React.FC<ManualNotificationTriggerProps> = ({ onClose }) => {
    const [form] = Form.useForm();
    const [selectedService, setSelectedService] = useState<string | null>(null);
    const [queryFilters, setQueryFilters] = useState<Record<string, any>>({});
    const [previewCount, setPreviewCount] = useState<number | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const { isLoading, sendManualTriggeredNotifications, resultSummary } = useNotificationTrigger();

    // Get list of available notification process services
    const { data: servicesData, isLoading: isLoadingServices } = useGetAllQueryServices();
    const queryServices = servicesData || [];

    // Reset filters when service changes
    useEffect(() => {
        setQueryFilters({});
        setPreviewCount(null);
    }, [selectedService]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            console.log(values);

            // Send using the selected notification process service
            await sendManualTriggeredNotifications(
                values.queryServiceName,
                values.templateName,
                values.platform,
                values.language,
                queryFilters
            );
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handlePreview = async () => {
        if (!selectedService) return;

        setIsPreviewLoading(true);
        try {
            const response = await previewTemplateParameters(
                selectedService,
                queryFilters,
                { limit: 3 }
            );
            setPreviewCount(response.data.length);

            // Show preview modal
            Modal.info({
                title: 'Notification Preview',
                content: (
                    <div>
                        <p>This notification would be sent to approximately {response.data.length} recipients.</p>
                        {response.data.length === 0 ? (
                            <p>No recipients match the current filter criteria.</p>
                        ) : (
                            <p>You can adjust the filters to target specific recipients.</p>
                        )}
                    </div>
                ),
                okText: 'Close'
            });
        } catch (error) {
            console.error('Error previewing notifications:', error);
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const handleFiltersChange = (filters: Record<string, any>) => {
        setQueryFilters(filters);
        // Reset preview count when filters change
        setPreviewCount(null);
    };

    // Show success result if we have sent notifications
    if (resultSummary) {
        return (
            <Card>
                <Result
                    status="success"
                    title="Notifications Sent Successfully"
                    subTitle={`${resultSummary.total} notifications sent at ${new Date(resultSummary.timestamp).toLocaleString()}`}
                    icon={<CheckCircleOutlined />}
                    extra={[
                        <Button type="primary" key="done" onClick={onClose}>
                            Done
                        </Button>
                    ]}
                />
            </Card>
        );
    }

    return (
        <Card title={
            <Space>
                <ApiOutlined />
                <span>Manual Notification Trigger</span>
            </Space>
        }>
            {isLoading ? (
                <div className="text-center py-12">
                    <Spin size="large" />
                    <div className="mt-4">
                        <Text>Processing notifications...</Text>
                    </div>
                </div>
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        platform: NotificationPlatforms.SMS,
                        language: Languages.EN
                    }}
                >
                    <Alert
                        message="Manual Notification Trigger"
                        description="Execute any registered notification process to send targeted notifications to customers."
                        type="info"
                        showIcon
                        className="mb-6"
                    />

                    <Divider>Process Selection</Divider>

                    <Form.Item
                        name="queryServiceName"
                        label="Notification Process"
                        rules={[{ required: true, message: 'Please select a notification process' }]}
                    >
                        <Select
                            placeholder="Select notification process"
                            loading={isLoadingServices}
                            onChange={(value) => setSelectedService(value)}
                        >
                            {queryServices.map(service => (
                                <Option key={service.name} value={service.name}>
                                    {service.displayName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {selectedService && (
                        <>
                            <DynamicFilterForm
                                serviceName={selectedService}
                                value={queryFilters}
                                onChange={handleFiltersChange}
                            />

                            <div className="mt-2 mb-4">
                                <Button
                                    onClick={handlePreview}
                                    loading={isPreviewLoading}
                                >
                                    Preview Recipients
                                </Button>
                            </div>
                        </>
                    )}

                    <Divider>Message Configuration</Divider>

                    <Form.Item
                        name="platform"
                        label="Platform"
                        rules={[{ required: true, message: 'Please select a platform' }]}
                    >
                        <PlatformSelector />
                    </Form.Item>

                    <Form.Item
                        name="language"
                        label="Language"
                        rules={[{ required: true, message: 'Please select a language' }]}
                    >
                        <Select placeholder="Select language">
                            <Option value={Languages.EN}>English</Option>
                            <Option value={Languages.SW}>Swahili</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="templateName"
                        label="Message Template"
                        rules={[{ required: true, message: 'Please select a template' }]}
                    >
                        <NotificationTemplateSelector />
                    </Form.Item>

                    <Divider />

                    <Form.Item className="mb-0">
                        <Space>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                icon={<SendOutlined />}
                            >
                                Send Notifications
                            </Button>
                            <Button onClick={onClose}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            )}
        </Card>
    );
};

export default ManualNotificationTrigger;